import { createContext, useContext, useEffect, useState, useCallback } from "react";

const I18nContext = createContext(null);

const STORAGE_KEY = "gbs_lang";

export const STRINGS = {
  en: {
    nav: { services: "Services", gallery: "Gallery", about: "About", contact: "Contact", callNow: "Call Now", admin: "Admin" },
    hero: { tagline: "Newark, NJ · Est. Auto & Truck Specialists", primaryCta: "Get a Free Estimate", secondaryCta: "WhatsApp Us", trust: "Certified Experts · Auto & Truck · Insurance Accepted" },
    services: { label: "What We Do", title: "Services Built for Cars & Trucks", subtitle: "Four core capabilities, one trusted shop." },
    gallery: { label: "Receipts", title: "Before & After", subtitle: "Real cars. Real damage. Real repairs.", before: "BEFORE", after: "AFTER" },
    about: { label: "About Us", aboutCta: "Visit Our Shop" },
    contact: {
      label: "Get in Touch",
      title: "Ready when you are.",
      subtitle: "Stop by the shop, call us, or send a WhatsApp. Estimates are free.",
      callPrimary: "Call Primary",
      callSecondary: "Call Secondary",
      whatsapp: "WhatsApp",
      directions: "Directions",
      hoursLabel: "Hours",
      addressLabel: "Address",
      phoneLabel: "Phone",
      formTitle: "Request a Free Estimate",
      formSubtitle: "Send us the details — we'll get back to you fast.",
      formName: "Full Name",
      formPhone: "Phone",
      formEmail: "Email (optional)",
      formVehicle: "Vehicle (year / make / model)",
      formMessage: "Tell us what happened",
      formSubmit: "Send Message",
      formSending: "Sending…",
      formSuccess: "Thanks! We received your message and will contact you soon.",
      formError: "Something went wrong. Please call us at the number above.",
    },
    footer: { rights: "All rights reserved.", builtFor: "Auto & Truck Repair, Newark NJ" },
    admin: {
      login: "Admin Login",
      email: "Email",
      password: "Password",
      signIn: "Sign in",
      signOut: "Sign out",
      dashboard: "GBS Admin",
      tabs: { site: "Site Content", services: "Services", gallery: "Gallery", messages: "Messages" },
      save: "Save changes",
      saved: "Saved",
      add: "Add new",
      delete: "Delete",
      edit: "Edit",
      cancel: "Cancel",
      uploadLogo: "Upload Logo",
      uploadImage: "Upload Image",
      orPasteUrl: "or paste image URL",
      logoUrl: "Logo (square) — used in navbar & hero",
      heroBgUrl: "Hero background image",
      preview: "Preview",
    },
  },
  es: {
    nav: { services: "Servicios", gallery: "Galería", about: "Nosotros", contact: "Contacto", callNow: "Llamar ahora", admin: "Admin" },
    hero: { tagline: "Newark, NJ · Especialistas en Autos y Camiones", primaryCta: "Cotización Gratis", secondaryCta: "Escríbenos WhatsApp", trust: "Expertos Certificados · Autos y Camiones · Aceptamos Seguros" },
    services: { label: "Lo Que Hacemos", title: "Servicios para Autos y Camiones", subtitle: "Cuatro especialidades, un solo taller de confianza." },
    gallery: { label: "Resultados", title: "Antes y Después", subtitle: "Autos reales. Daños reales. Reparaciones reales.", before: "ANTES", after: "DESPUÉS" },
    about: { label: "Sobre Nosotros", aboutCta: "Visita el Taller" },
    contact: {
      label: "Contáctanos",
      title: "Listos cuando tú lo estés.",
      subtitle: "Pasa por el taller, llámanos o envíanos un WhatsApp. La cotización es gratis.",
      callPrimary: "Llamada Principal",
      callSecondary: "Llamada Secundaria",
      whatsapp: "WhatsApp",
      directions: "Cómo llegar",
      hoursLabel: "Horario",
      addressLabel: "Dirección",
      phoneLabel: "Teléfono",
      formTitle: "Solicita una Cotización Gratis",
      formSubtitle: "Envíanos los detalles — te contactaremos pronto.",
      formName: "Nombre Completo",
      formPhone: "Teléfono",
      formEmail: "Correo (opcional)",
      formVehicle: "Vehículo (año / marca / modelo)",
      formMessage: "Cuéntanos qué pasó",
      formSubmit: "Enviar Mensaje",
      formSending: "Enviando…",
      formSuccess: "¡Gracias! Recibimos tu mensaje y te contactaremos pronto.",
      formError: "Algo salió mal. Por favor llámanos al teléfono de arriba.",
    },
    footer: { rights: "Todos los derechos reservados.", builtFor: "Reparación de Autos y Camiones, Newark NJ" },
    admin: {
      login: "Acceso Admin",
      email: "Correo",
      password: "Contraseña",
      signIn: "Entrar",
      signOut: "Salir",
      dashboard: "Admin GBS",
      tabs: { site: "Contenido del sitio", services: "Servicios", gallery: "Galería", messages: "Mensajes" },
      save: "Guardar cambios",
      saved: "Guardado",
      add: "Agregar nuevo",
      delete: "Eliminar",
      edit: "Editar",
      cancel: "Cancelar",
      uploadLogo: "Subir Logo",
      uploadImage: "Subir Imagen",
      orPasteUrl: "o pega una URL de imagen",
      logoUrl: "Logo (cuadrado) — usado en navbar y hero",
      heroBgUrl: "Imagen de fondo del Hero",
      preview: "Vista previa",
    },
  },
};

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem(STORAGE_KEY) || "en");

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (path) => {
      const keys = path.split(".");
      let cur = STRINGS[lang];
      for (const k of keys) {
        if (cur && typeof cur === "object" && k in cur) cur = cur[k];
        else return path;
      }
      return cur;
    },
    [lang]
  );

  // pick bilingual field { en, es } gracefully
  const pick = useCallback(
    (obj) => {
      if (!obj) return "";
      if (typeof obj === "string") return obj;
      return obj[lang] || obj.en || obj.es || "";
    },
    [lang]
  );

  // pick suffix field eg. title_en / title_es
  const pickSuffix = useCallback(
    (obj, base) => {
      if (!obj) return "";
      return obj[`${base}_${lang}`] || obj[`${base}_en`] || obj[`${base}_es`] || "";
    },
    [lang]
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t, pick, pickSuffix }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
